import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	SimpleChanges,
	ViewChild,
	OnInit,
	OnChanges,
	OnDestroy
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subscription, map, of, startWith } from 'rxjs';
import { FormlyService } from '@nx-ahs/formly/shared';
import { ActivatedRoute } from '@angular/router';
import { BlockType, BuilderService } from '../../../../services/builder.service';
import { FormlyBuilderApiService } from '../../../../services/formly-builder-api.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ILanguageConfig } from '@nx-ahs/base-translate';

@Component({
	selector: 'nx-ahs-toolbar-save',
	templateUrl: './toolbar-save.component.html',
	styleUrl: './toolbar-save.component.css',
	standalone: false
})
export class ToolbarSaveComponent implements OnInit, OnChanges, OnDestroy {
	@ViewChild('controlsInput')
	controlsInput: ElementRef<HTMLInputElement>;

	@Input()
	blockControlDisabled: boolean;

	@Input()
	hasChanges: boolean;

	@Input()
	isJsonValid: boolean;

	@Output()
	blockTypeSelected = new EventEmitter<BlockType>(null);

	@Output()
	saveClicked = new EventEmitter<void>(null);

	@Output()
	importFromTripettoClicked = new EventEmitter<void>();

	@Output()
	manageTranslationsClicked = new EventEmitter<void>(null);

	public myControl = new FormControl('');
	public blocks: BlockType[] = []; //['One', 'Two', 'Three', 'One hundred forty five'];
	public filteredOptions: Observable<BlockType[]>;

	public data: any;

	private subscriptions: Subscription[] = [];
	private scriptId: number;

	constructor(
		private route: ActivatedRoute,
		private formlyService: FormlyService,
		private builderApiService: FormlyBuilderApiService,
		private builderService: BuilderService,
		private matDialog: MatDialog,
		private snackBar: MatSnackBar
	) {}

	ngOnInit() {
		this.scriptId = this.route?.snapshot?.params['scriptId'];

		// this loads the custom types into the 'Quick Add' dropdown
		// Sort alphabetically by 'name'
		this.blocks = this.builderService.blocks.sort((a, b) => a.name.localeCompare(b.name));

		this.formlyService
			.isReady()
			.then(() => {
				return this.formlyService.getScript(this.scriptId);
			})
			.then((result) => {
				this.data = result;
			});

		this.filteredOptions = this.myControl.valueChanges.pipe(
			startWith(''),
			map((value) => this._filter(value || ''))
		);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.blockControlDisabled?.currentValue != null && !changes?.blockControlDisabled?.currentValue) {
			this.myControl.disable();
		} else {
			this.myControl.enable();
		}
	}

	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	public blockSelected($event: MatAutocompleteSelectedEvent): void {
		const obj = $event.option;
		this.blockTypeSelected.emit(obj.value as BlockType);
		this.myControl.setValue('');
	}

	public async editScriptInfo(): Promise<any> {
		const overrides = {
			name: {
				props: {
					value: this.data?.name
				}
			},
			description: {
				props: {
					value: this.data?.description
				}
			},
			notes: {
				props: {
					value: this.data?.notes
				}
			}
		};

		const data = await this.builderApiService.openFormlyDialog(
			{
				scriptId: 2,
				title: 'Script Info',
				overrides: overrides,
				clientOnly: true
			},
			this.matDialog
		);

		if (data) {
			const updated = await this.builderApiService.saveScriptInfo({
				scriptId: this.scriptId,
				script: {
					scriptId: this.scriptId,
					name: data?.model?.name,
					description: data?.model?.description,
					notes: data?.model?.notes
				}
			});

			if (updated) {
				this.data.name = data?.model?.name;
				this.data.description = data?.model?.description;
				this.data.notes = data?.model?.notes;

				this.showSuccess('Successfully saved', 3000);
			}
		}
	}

	public filter(): void {
		const filterValue = this.controlsInput.nativeElement.value.toLowerCase();
		this.filteredOptions = of(this.blocks.filter((o) => o.name.toLowerCase().includes(filterValue)));
	}

	public save(): void {
		this.saveClicked.emit();
	}

	public importFromTripetto(): void {
		this.importFromTripettoClicked.emit();
	}

	public openTranslationsManager(): void {
		this.manageTranslationsClicked.emit();
	}

	private _filter(value: string): BlockType[] {
		let results: BlockType[] = [];

		if (typeof value == 'string') {
			const filterValue = value.toLowerCase();

			results = this.blocks.filter((option) => option.name.toLowerCase().includes(filterValue));
		} else {
			const filterValue = '';
			results = this.blocks.filter((option) => option.name.toLowerCase().includes(filterValue));
		}

		return results;
	}

	private showSuccess(message: string, duration: number = 3000) {
		// Default duration is 3 seconds
		this.snackBar.open(message, 'Close', {
			duration, // Time limit in milliseconds
			panelClass: ['success-snackbar']
		});
	}
}
